import { useRef, useEffect, useCallback } from "react";

// How far outside a shape's 0–1 bounds the cursor still "selects" it
// during hover-reveal mode (prevents flickery gaps between shapes).
const HOVER_PAD_FRACTION = 0.06;

// Deterministic pseudo-random in [0,1) from a shape's integer id.
// Same shape → same value (no flicker), neighbours look unrelated.
// `salt` pulls independent channels from one seed.
const hash = (n, salt = 0) => {
  const x = Math.sin((n + salt) * 127.1) * 43758.5453;
  return x - Math.floor(x);
};

// Stamp deterministic jitter custom properties for the hero transform.
// Pure compositor transforms (rotate/scale/skew) — effectively free.
function stampHeroJitter(shape) {
  const id = parseFloat(shape.dataset.shapeId) || 0;
  shape.style.setProperty("--jit-rot", `${(hash(id) - 0.5) * 5}deg`);
  shape.style.setProperty("--jit-sx", `${1 + (hash(id, 17) - 0.5) * 0.08}`);
  shape.style.setProperty("--jit-sy", `${1 + (hash(id, 53) - 0.5) * 0.08}`);
  shape.style.setProperty("--jit-skew", `${(hash(id, 91) - 0.5) * 7}deg`);
}

/** Create a fresh shape state object. */
function createShapeState() {
  return {
    prevActiveIds: new Set(),
    prevHeroId: null,
    pendingActiveIds: new Set(),
    pendingHeroId: null,
    currentEncompassingIds: new Set(),
    currentHeroBounds: { start: 0, end: 0 }
  };
}

// Pure helper — finds encompassing shapes & hero at a given fraction.
function findShapesAtFraction(shapes, fraction, pad = 0) {
  const activeIds = new Set();
  let heroId = null;
  let heroDist = Infinity;

  shapes.forEach(shape => {
    const start = parseFloat(shape.dataset.fractionStart);
    const end = parseFloat(shape.dataset.fractionEnd);
    if (fraction >= start - pad && fraction <= end + pad) {
      activeIds.add(shape.dataset.shapeId);
      const dist = Math.abs(fraction - (start + end) / 2);
      if (dist < heroDist) {
        heroDist = dist;
        heroId = shape.dataset.shapeId;
      }
    }
  });

  return { activeIds, heroId };
}

/**
 * Diff next vs previous active/hero sets and apply classList changes.
 * Mutates state.prevActiveIds, state.prevHeroId, and state.currentHeroBounds.
 * Returns true if any DOM change occurred.
 */
function applyShapeClasses(shapes, nextActiveIds, nextHeroId, state) {
  const { prevActiveIds, prevHeroId } = state;

  const heroChanged = nextHeroId !== prevHeroId;
  let activeChanged = nextActiveIds.size !== prevActiveIds.size;
  if (!activeChanged) {
    for (const id of nextActiveIds) {
      if (!prevActiveIds.has(id)) {
        activeChanged = true;
        break;
      }
    }
  }
  if (!heroChanged && !activeChanged) return false;

  shapes.forEach(shape => {
    const id = shape.dataset.shapeId;

    if (activeChanged) {
      if (nextActiveIds.has(id)) shape.classList.add("shape-active");
      else shape.classList.remove("shape-active");
    }

    if (heroChanged) {
      if (id === prevHeroId) shape.classList.remove("shape-hero");
      if (id === nextHeroId) {
        shape.classList.add("shape-hero");
        stampHeroJitter(shape);
        // Move to end of parent so it draws on top (SVG has no z-index)
        shape.parentNode.appendChild(shape);
        state.currentHeroBounds = {
          start: parseFloat(shape.dataset.fractionStart),
          end: parseFloat(shape.dataset.fractionEnd)
        };
      }
    }
  });

  state.prevActiveIds = new Set(nextActiveIds);
  state.prevHeroId = nextHeroId;
  return true;
}

/**
 * useShapeActivation
 *
 * Owns the shared shape state and manages `.shape-active` / `.shape-hero`
 * CSS classes across both hover-reveal and playback modes
 *
 * Hover-reveal: immediate DOM class updates driven by cursor X.
 * Playback: accumulates candidates into shapeState for the pulse loop
 * to promote on audio onsets via promoteShapes()
 */
export function useShapeActivation({
  topShapesRef,
  svgContainer,
  isPlayingProgress,
  isActive,
  markup
}) {
  const shapeState = useRef(createShapeState());
  const hoverFraction = useRef(0);

  // Closure-safe ref — event handlers capture this
  const isPlayingProgressRef = useRef(false);
  isPlayingProgressRef.current = isPlayingProgress;

  // Reset stale shape-tracking on markup change (track switch)
  const prevMarkup = useRef(markup);
  if (markup !== prevMarkup.current) {
    prevMarkup.current = markup;
    Object.assign(shapeState.current, createShapeState());
  }

  // ── Reset all shape state + DOM classes ────────────────────────────
  const resetShapeState = useCallback(() => {
    topShapesRef.current.forEach(el => {
      el.classList.remove("shape-active", "shape-hero");
    });
    Object.assign(shapeState.current, createShapeState());
  }, [topShapesRef]);

  // ── Promote shapes (swap pending → committed on audio onset) ──────
  const promoteShapes = useCallback(() => {
    if (!svgContainer.current) return;
    const state = shapeState.current;
    applyShapeClasses(
      topShapesRef.current,
      state.pendingActiveIds,
      state.pendingHeroId,
      state
    );
    // Reset accumulator to just the shapes encompassing right now —
    // exited shapes will naturally fall out on the next onset.
    state.pendingActiveIds = new Set(state.currentEncompassingIds);
  }, [svgContainer, topShapesRef]);

  // ── Accumulate shape candidates for pulse-gated promotion ─────────
  // No DOM mutation — candidates are written to shapeState for the rAF
  // onset loop to promote via promoteShapes()
  const updateShapeClasses = useCallback(
    fraction => {
      if (!svgContainer.current) return;
      const { activeIds, heroId } = findShapesAtFraction(
        topShapesRef.current,
        fraction
      );
      const state = shapeState.current;
      for (const id of activeIds) state.pendingActiveIds.add(id);
      state.currentEncompassingIds = activeIds;
      if (heroId) state.pendingHeroId = heroId;
    },
    [svgContainer, topShapesRef]
  );

  // ── Hover: immediate X-based shape activation ─────────────────────
  const handleShapeActivation = e => {
    const container = svgContainer.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    hoverFraction.current = (e.clientX - rect.left) / rect.width;

    // During playback, shape activation is driven by the rAF pulse loop.
    if (isPlayingProgressRef.current) return;

    const { activeIds, heroId } = findShapesAtFraction(
      topShapesRef.current,
      hoverFraction.current,
      HOVER_PAD_FRACTION
    );
    applyShapeClasses(
      topShapesRef.current,
      activeIds,
      heroId,
      shapeState.current
    );
  };

  const handleShapeDeactivation = useCallback(() => {
    hoverFraction.current = 0;
    topShapesRef.current.forEach(el => {
      el.classList.remove("shape-active", "shape-hero");
    });
    Object.assign(shapeState.current, createShapeState());
  }, [topShapesRef]);

  // Safety net: force-clean shapes whenever the tier drops from active.
  // Handles edge cases where onMouseLeave didn't fire (fast scroll,
  // overlapping elements, React batching delays).
  const prevIsActive = useRef(false);
  useEffect(() => {
    if (prevIsActive.current && !isActive) {
      handleShapeDeactivation();
    }
    prevIsActive.current = isActive;
  }, [isActive, handleShapeDeactivation]);

  return {
    handleShapeActivation,
    handleShapeDeactivation,
    hoverFraction,
    promoteShapes,
    updateShapeClasses,
    resetShapeState,
    shapeState
  };
}
